import React, { useState, useEffect, useCallback } from "react";
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, getDoc, getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { generarEImprimirEtiquetas } from "../services/etiquetasPDF";
import QrScanner from "../../../components/QrScanner";
import EscaneoContinuoModal from "../components/EscaneoContinuoModal";

// ── Helpers ────────────────────────────────────────────────
function fmtFecha(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleDateString("es-UY", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function fmtHora(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

function estadoLabel(estado) {
  const map = {
    PENDIENTE_ASIGNAR: "Pendiente asignar",
    ASIGNADO: "Asignado",
    EN_PREPARACION: "En preparación",
    CON_ERROR: "Con error",
    ERROR_CONFIRMADO: "Con error",
    PREPARADO: "Preparado",
    CONTROLADO: "Controlado",
  };
  return map[estado] || estado?.replace(/_/g, " ") || "—";
}

// ── Modal de escaneo de bultos ────────────────────────────
function ScanModal({ pedido, onClose, onCargado }) {
  const totalBultos = Number(pedido.bultos) || 1;
  const [escaneados, setEscaneados] = useState(new Set());
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [printedNow, setPrintedNow] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);

  const etiquetasListas = !!pedido.etiquetasImpresas || printedNow;

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw);
    const match = texto.match(/^(.+)\|(\d+)\/(\d+)$/);
    if (!match) { setError(`QR no reconocido: "${texto.slice(0, 40)}"`); return; }
    const [, pid, bultoN] = match;
    if (pid !== pedido.id) { setError(`Bulto de otro pedido (#${pid})`); return; }
    const n = Number(bultoN);
    if (escaneados.has(n)) { setError(`Bulto ${n} ya escaneado`); return; }
    setEscaneados(prev => new Set([...prev, n]));
  }, [pedido.id, escaneados]);

  const todosEscaneados = escaneados.size === totalBultos;

  async function imprimir() {
    setImprimiendo(true);
    try {
      await generarEImprimirEtiquetas([{
        pedidoId: pedido.id,
        numero: pedido.numero || pedido.id,
        cliente: pedido.cliente || "",
        direccion: pedido.metodoEntrega || "CAMION",
        localidad: pedido.deposito || "R8",
        bultos: totalBultos,
      }]);
      setPrintedNow(true);
      try { await updateDoc(doc(db, "pedidos_despachados", pedido.id), { etiquetasImpresas: true }); } catch (_) { /* best-effort */ }
    } catch (e) {
      setError("No se pudieron imprimir las etiquetas");
    } finally {
      setImprimiendo(false);
    }
  }

  async function confirmarCarga() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "pedidos_despachados", pedido.id), {
        cargaEstado: "CARGADO",
        cargadoAt: serverTimestamp(),
        bultosEscaneados: [...escaneados].sort((a, b) => a - b),
      });
      onCargado();
    } catch (e) {
      setError("Error al guardar: " + (e?.message || e));
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px 20px 32px", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>📷 Cargar — #{pedido.numero}</h3>
            <p style={{ margin: "3px 0 0", fontSize: "13px", color: "#64748b" }}>{pedido.cliente}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
        </div>

        {!etiquetasListas ? (
          /* Paso previo: faltan imprimir las etiquetas */
          <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: "12px", padding: "16px" }}>
            <p style={{ margin: "0 0 4px", fontSize: "0.95rem", fontWeight: 700, color: "#92400e" }}>
              ⚠️ Faltan imprimir las etiquetas
            </p>
            <p style={{ margin: "0 0 14px", fontSize: "0.83rem", color: "#a16207" }}>
              {totalBultos} bulto{totalBultos !== 1 ? "s" : ""}. Imprimí, pegá las etiquetas y después escaneá cada una.
            </p>
            <button
              onClick={imprimir}
              disabled={imprimiendo}
              style={{
                width: "100%", padding: "13px", borderRadius: "12px", border: "none",
                background: imprimiendo ? "#e2e8f0" : "#1d4ed8", color: imprimiendo ? "#94a3b8" : "#fff",
                fontWeight: 700, fontSize: "0.95rem", cursor: imprimiendo ? "not-allowed" : "pointer",
              }}
            >
              {imprimiendo ? "Imprimiendo…" : `🖨️ Imprimir ${totalBultos} etiqueta${totalBultos !== 1 ? "s" : ""}`}
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
              {Array.from({ length: totalBultos }, (_, i) => i + 1).map(n => (
                <div key={n} style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: escaneados.has(n) ? "#16a34a" : "#f1f5f9",
                  color: escaneados.has(n) ? "#fff" : "#94a3b8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: "14px",
                  border: `1.5px solid ${escaneados.has(n) ? "#16a34a" : "#e2e8f0"}`,
                }}>{escaneados.has(n) ? "✓" : n}</div>
              ))}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#64748b" }}>
              {escaneados.size}/{totalBultos} bultos escaneados
            </p>
            {error && (
              <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "8px", padding: "10px 14px", color: "#dc2626", fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>
                ⚠️ {error}
              </div>
            )}
            {!todosEscaneados && <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />}
            {todosEscaneados && (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", textAlign: "center" }}>
                <div style={{ fontSize: "28px", marginBottom: "6px" }}>✅</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "15px" }}>Todos los bultos escaneados</p>
              </div>
            )}
            <button
              onClick={confirmarCarga}
              disabled={!todosEscaneados || saving}
              style={{
                width: "100%", padding: "15px", borderRadius: "12px", border: "none",
                fontWeight: 700, fontSize: "15px", marginTop: "8px",
                background: todosEscaneados && !saving ? "#0f172a" : "#e2e8f0",
                color: todosEscaneados && !saving ? "#fff" : "#94a3b8",
                cursor: todosEscaneados && !saving ? "pointer" : "not-allowed",
              }}
            >
              {saving ? "Confirmando…" : "✓ Confirmar carga"}
            </button>
            <button
              onClick={imprimir}
              disabled={imprimiendo}
              style={{ marginTop: "12px", width: "100%", background: "none", border: "none", color: "#64748b", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
            >
              🖨️ Reimprimir etiquetas
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Modal de finalizar carga (doble confirmación) ─────────
function FinalizarModal({ cargados, despachadosSinCargar, pendientes, onClose, onConfirm, finalizando }) {
  const totalAfuera = despachadosSinCargar.length + pendientes.length;
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px 20px 28px", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>🚚 Finalizar carga</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
          Vas a cerrar este viaje. Lo que escaneaste queda registrado como cargado y el camión sale.
        </p>

        {/* Cargados */}
        <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px", padding: "12px 14px", marginBottom: "12px" }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "14px", color: "#166534" }}>
            ✅ Se cargaron {cargados.length} pedido{cargados.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Lo que queda afuera */}
        {totalAfuera > 0 ? (
          <div style={{ background: "#fff7ed", border: "1.5px solid #fdba74", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px" }}>
            <p style={{ margin: "0 0 10px", fontWeight: 800, fontSize: "14px", color: "#9a3412" }}>
              ⚠️ Quedan afuera {totalAfuera} pedido{totalAfuera !== 1 ? "s" : ""}
            </p>

            {despachadosSinCargar.length > 0 && (
              <div style={{ marginBottom: pendientes.length > 0 ? "12px" : 0 }}>
                <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  📦 Despachados pero sin escanear ({despachadosSinCargar.length})
                </p>
                {despachadosSinCargar.map(p => (
                  <div key={p.id} style={{ fontSize: "13px", color: "#7c2d12", padding: "3px 0" }}>
                    <strong>{p.numero || p.id}</strong> — {p.cliente}
                    <span style={{ color: "#c2410c", fontSize: "12px" }}> → vuelve a pendiente de carga</span>
                  </div>
                ))}
              </div>
            )}

            {pendientes.length > 0 && (
              <div>
                <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  🕐 Todavía sin despachar ({pendientes.length})
                </p>
                {pendientes.map(p => (
                  <div key={p.id} style={{ fontSize: "13px", color: "#7c2d12", padding: "3px 0" }}>
                    <strong>{p.numero || p.id}</strong> — {p.cliente}
                    <span style={{ color: "#c2410c", fontSize: "12px" }}> · {estadoLabel(p.estado)}</span>
                  </div>
                ))}
              </div>
            )}

            <p style={{ margin: "12px 0 0", fontSize: "12px", color: "#9a3412", fontStyle: "italic" }}>
              Estos pedidos quedan disponibles para sumar al próximo viaje.
            </p>
          </div>
        ) : (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", fontSize: "13px", color: "#166534" }}>
            No queda ningún pedido afuera. Todo lo de este viaje está cargado.
          </div>
        )}

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={onClose}
            disabled={finalizando}
            style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, fontSize: "14px", color: "#475569", cursor: "pointer" }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={finalizando}
            style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", background: finalizando ? "#94a3b8" : "#dc2626", color: "#fff", fontWeight: 700, fontSize: "14px", cursor: finalizando ? "not-allowed" : "pointer" }}
          >
            {finalizando ? "Finalizando…" : "Sí, finalizar carga"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta de pedido despachado ──────────────────────────
function PedidoCard({ p, onScan, readonly }) {
  const esCargado      = p.cargaEstado === "CARGADO";
  const esDiscrepancia = p.cargaEstado === "DISCREPANCIA";

  return (
    <div style={{
      background:   esCargado ? "#f0fdf4" : esDiscrepancia ? "#fff7ed" : "#fff",
      border:       "1.5px solid",
      borderColor:  esCargado ? "#86efac" : esDiscrepancia ? "#fed7aa" : "#e2e8f0",
      borderRadius: "12px", padding: "14px 16px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>{p.numero || p.id}</span>
            {p.despachadoAt && <span style={{ fontSize: "11px", color: "#94a3b8" }}>{fmtHora(p.despachadoAt)}</span>}
          </div>
          <div style={{ fontSize: "13px", color: "#334155", marginTop: "2px", fontWeight: 500 }}>{p.cliente}</div>
          <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>📫 {p.bultos ?? "—"} bulto{Number(p.bultos) !== 1 ? "s" : ""}</span>
          </div>
          {p.bultosEscaneados?.length > 0 && (
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
              Escaneados: {p.bultosEscaneados.join(", ")}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          {esCargado ? (
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#166534", background: "#dcfce7", border: "1px solid #86efac", borderRadius: "8px", padding: "6px 12px" }}>✓ Cargado</span>
          ) : esDiscrepancia ? (
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "8px", padding: "6px 10px" }}>⚠️ Diferencia</span>
          ) : readonly ? (
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#94a3b8", background: "#f1f5f9", borderRadius: "8px", padding: "6px 10px" }}>Sin cargar</span>
          ) : (
            <button
              onClick={() => onScan(p)}
              style={{ padding: "9px 16px", fontSize: "13px", fontWeight: 700, background: "#0f172a", color: "#fff", border: "none", borderRadius: "9px", cursor: "pointer" }}
            >📷 Escanear</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ────────────────────────────────────────
export default function ControlCamion() {
  const { camionId } = useParams();
  const { profile }  = useAuth();
  const navigate     = useNavigate();

  const [camion,        setCamion]        = useState(null);
  const [loadingCamion, setLoadingCamion] = useState(true);
  const [despachados,   setDespachados]   = useState([]);
  const [pendientes,    setPendientes]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [toastMsg,      setToastMsg]      = useState(null);
  const [scanPedido,    setScanPedido]    = useState(null);
  const [imprimiendo,   setImprimiendo]   = useState(false);
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [finalizando,   setFinalizando]   = useState(false);
  const [showEscaneo,   setShowEscaneo]   = useState(false);

  // Picker: agregar pedidos al camión
  const [showPicker,    setShowPicker]    = useState(false);
  const [pickerItems,   setPickerItems]   = useState([]);
  const [loadingPicker, setLoadingPicker] = useState(false);
  const [asignando,     setAsignando]     = useState(null);
  const [searchPicker,  setSearchPicker]  = useState("");

  const deposito = profile?.deposito || null;
  const esCerrado = camion?.estado === "CERRADO";

  const showToast = (msg, tipo = "ok") => {
    setToastMsg({ msg, tipo });
    setTimeout(() => setToastMsg(null), 3000);
  };

  // ── Cargar metadata del camión (en vivo, para reflejar el cierre) ──
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "camiones", camionId), snap => {
      if (snap.exists()) setCamion({ id: snap.id, ...snap.data() });
      setLoadingCamion(false);
    });
    return () => unsub();
  }, [camionId]);

  // ── Listener: pedidos despachados de este camión ─────────
  useEffect(() => {
    const q = query(
      collection(db, "pedidos_despachados"),
      where("camionId", "==", camionId),
    );
    const unsub = onSnapshot(q, snap => {
      setDespachados(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [camionId]);

  // ── Listener: pedidos pendientes de este camión ──────────
  useEffect(() => {
    const q = query(
      collection(db, "pedidos"),
      where("camionId", "==", camionId),
    );
    const unsub = onSnapshot(q, snap => {
      setPendientes(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.estado !== "ANULADO"),
      );
    });
    return () => unsub();
  }, [camionId]);

  // ── Abrir picker: pedidos sin camión (sin despachar + despachados sin cargar) ──
  async function abrirPicker() {
    setSearchPicker("");
    setShowPicker(true);
    setLoadingPicker(true);
    try {
      const qPed = deposito
        ? query(collection(db, "pedidos"), where("deposito", "==", deposito))
        : query(collection(db, "pedidos"));
      const qDesp = deposito
        ? query(collection(db, "pedidos_despachados"), where("deposito", "==", deposito))
        : query(collection(db, "pedidos_despachados"));
      const [snapPed, snapDesp] = await Promise.all([getDocs(qPed), getDocs(qDesp)]);

      // Solo pedidos del MISMO tipo que el viaje (Gira con Gira, Camión con Camión).
      const tipoViaje = camion?.tipo || "CAMION";
      const libresPed = snapPed.docs
        .map(d => ({ id: d.id, _col: "pedidos", ...d.data() }))
        .filter(p => !p.camionId && p.estado !== "ANULADO" && p.metodoEntrega === tipoViaje);
      // Despachados sin cargar que quedaron de viajes anteriores (mismo tipo)
      const libresDesp = snapDesp.docs
        .map(d => ({ id: d.id, _col: "pedidos_despachados", ...d.data() }))
        .filter(p => !p.camionId && p.cargaEstado !== "CARGADO" && p.metodoEntrega === tipoViaje);

      // Despachados primero (ya están listos para cargar)
      setPickerItems([...libresDesp, ...libresPed]);
    } finally {
      setLoadingPicker(false);
    }
  }

  // ── Asignar pedido a este camión ─────────────────────────
  async function asignarPedido(item) {
    setAsignando(item.id);
    try {
      const col = item._col || "pedidos";
      const tipoViaje = camion?.tipo || "CAMION";
      const patch = col === "pedidos"
        ? { camionId, metodoEntrega: tipoViaje }
        : { camionId }; // los despachados ya tienen metodoEntrega
      await updateDoc(doc(db, col, item.id), patch);
      setPickerItems(prev => prev.filter(p => p.id !== item.id));
      showToast(`Pedido agregado a ${tipoViaje === "GIRA" ? "la gira" : "el camión"}`);
    } catch (e) {
      showToast("Error al asignar", "error");
    } finally {
      setAsignando(null);
    }
  }

  // ── Quitar pedido del camión ─────────────────────────────
  async function quitarPedido(pedidoId) {
    try {
      await updateDoc(doc(db, "pedidos", pedidoId), {
        camionId:      null,
        metodoEntrega: null,
      });
      showToast("Pedido quitado del camión");
    } catch (e) {
      showToast("Error al quitar", "error");
    }
  }

  // ── Finalizar carga: cierra el viaje y suelta lo no cargado ──
  async function finalizarCarga() {
    setFinalizando(true);
    try {
      // 1. Despachados sin cargar → soltar del camión (vuelven a pendiente de carga)
      const sinCargar = despachados.filter(p => p.cargaEstado !== "CARGADO");
      await Promise.all(sinCargar.map(p =>
        updateDoc(doc(db, "pedidos_despachados", p.id), { camionId: null })
      ));
      // 2. Pendientes (sin despachar) → soltar del camión (conservan método CAMION)
      await Promise.all(pendientes.map(p =>
        updateDoc(doc(db, "pedidos", p.id), { camionId: null })
      ));
      // 3. Cerrar el camión
      const cargadosCount = despachados.filter(p => p.cargaEstado === "CARGADO").length;
      await updateDoc(doc(db, "camiones", camionId), {
        estado: "CERRADO",
        finalizadoAt: serverTimestamp(),
        finalizadoPor: profile?.uid || null,
        finalizadoPorNombre: profile?.nombre || profile?.displayName || null,
        totalCargados: cargadosCount,
      });
      setShowFinalizar(false);
      showToast(`Carga finalizada — ${cargadosCount} pedido${cargadosCount !== 1 ? "s" : ""} cargado${cargadosCount !== 1 ? "s" : ""}`);
    } catch (e) {
      showToast("Error al finalizar: " + (e?.message || e), "error");
    } finally {
      setFinalizando(false);
    }
  }

  // ── Imprimir etiquetas para pendientes CONTROLADO con bultos ──
  async function imprimirEtiquetas() {
    // Despachados de este viaje, sin cargar y sin etiquetas impresas (post-despacho).
    const paraimprimir = despachados.filter(p => p.cargaEstado !== "CARGADO" && Number(p.bultos) > 0 && !p.etiquetasImpresas);
    if (paraimprimir.length === 0) { showToast("No hay despachados pendientes de imprimir", "warn"); return; }
    setImprimiendo(true);
    try {
      const items = paraimprimir.map(p => ({
        pedidoId: p.id,
        numero: p.numero || p.id,
        cliente: p.cliente || "",
        direccion: p.metodoEntrega || "CAMION",
        localidad: p.deposito || "",
        bultos: Number(p.bultos) || 1,
      }));
      await generarEImprimirEtiquetas(items);
      await Promise.all(paraimprimir.map(p =>
        updateDoc(doc(db, "pedidos_despachados", p.id), { etiquetasImpresas: true })
      ));
      showToast(`Etiquetas enviadas a imprimir (${paraimprimir.length} pedido${paraimprimir.length !== 1 ? "s" : ""})`);
    } catch (e) {
      showToast("Error al imprimir: " + (e?.message || e), "error");
    } finally {
      setImprimiendo(false);
    }
  }

  // ── Filtro del picker ────────────────────────────────────
  const sq = searchPicker.toLowerCase().trim();
  const pickerFiltrado = sq
    ? pickerItems.filter(p =>
        (p.numero || p.id).toLowerCase().includes(sq) ||
        (p.cliente || "").toLowerCase().includes(sq),
      )
    : pickerItems;

  // ── Totales ───────────────────────────────────────────────
  const totalDespachados   = despachados.length;
  const cargados           = despachados.filter(p => p.cargaEstado === "CARGADO");
  const despachadosSinCargar = despachados.filter(p => p.cargaEstado !== "CARGADO");
  const totalCargados      = cargados.length;
  const todoCargado        = totalDespachados > 0 && totalCargados === totalDespachados;
  const totalPendientes    = pendientes.length;
  const pendControlado     = despachados.filter(p => p.cargaEstado !== "CARGADO" && Number(p.bultos) > 0 && !p.etiquetasImpresas).length;

  if (loadingCamion) {
    return <div className="container screen-center">⏳ Cargando…</div>;
  }

  if (!camion) {
    return (
      <div className="container screen-center">
        <p>Camión no encontrado.</p>
        <button onClick={() => navigate("/encargado/camiones")} className="btn btn--ghost">
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingBottom: "90px" }}>

      {/* Toast */}
      {toastMsg && (
        <div className="toast" style={{
          background: toastMsg.tipo === "ok" ? "var(--ok)" : toastMsg.tipo === "warn" ? "#f59e0b" : "var(--error)",
        }}>
          {toastMsg.msg}
        </div>
      )}

      {/* ── Header ── */}
      <header className="topbar card" style={{ marginBottom: "16px" }} id="control-header">
        <button onClick={() => navigate("/encargado/cola-despacho")} className="btn btn--ghost btn-sm">
          ⬅ Volver
        </button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>🚚 {camion.descripcion}</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            Salida: {fmtFecha(camion.fechaSalida)}
          </p>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
          {esCerrado && (
            <span className="pill" style={{ background: "#e2e8f0", color: "#475569" }}>✓ Cerrado</span>
          )}
          {totalDespachados > 0 && (
            <span className={`pill ${todoCargado ? "pill--ok" : "pill--warn"}`}>
              {totalCargados}/{totalDespachados} cargados
            </span>
          )}
          {!esCerrado && totalPendientes > 0 && (
            <span className="pill" style={{ background: "#eff6ff", color: "#1d4ed8" }}>
              {totalPendientes} pendiente{totalPendientes !== 1 ? "s" : ""}
            </span>
          )}
          {!esCerrado && pendControlado > 0 && (
            <button
              onClick={imprimirEtiquetas}
              disabled={imprimiendo}
              className="btn btn--primary btn-sm"
            >
              {imprimiendo ? "…" : `🖨️ Etiquetas (${pendControlado})`}
            </button>
          )}
          <button onClick={() => window.print()} className="btn btn--ghost btn-sm" title="Imprimir manifiesto">
            📋
          </button>
        </div>
      </header>

      {/* ── Banner viaje cerrado ── */}
      {esCerrado && (
        <div style={{
          background: "#f1f5f9", border: "1.5px solid #cbd5e1",
          borderRadius: "12px", padding: "12px 16px", marginBottom: "16px",
          fontSize: "13px", color: "#475569",
        }}>
          🔒 <strong>Viaje cerrado</strong>{camion.finalizadoAt ? ` el ${fmtFecha(camion.finalizadoAt)} ${fmtHora(camion.finalizadoAt)}` : ""}.
          Se cargaron {totalCargados} pedido{totalCargados !== 1 ? "s" : ""}. Ya no se pueden agregar ni escanear pedidos.
        </div>
      )}

      {/* ── Estado vacío ── */}
      {!loading && totalDespachados === 0 && totalPendientes === 0 && !esCerrado && (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
          <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>
            No hay pedidos en este camión
          </strong>
          <span style={{ fontSize: "13px" }}>
            Usá el botón <strong>+ Agregar pedidos</strong> para asignar pedidos a esta salida
          </span>
        </div>
      )}

      {/* Escaneo continuo: escanear todos los bultos seguidos sin entrar a cada pedido */}
      {!esCerrado && despachadosSinCargar.length > 0 && (
        <button
          onClick={() => setShowEscaneo(true)}
          style={{
            width: "100%", padding: "14px", marginBottom: "16px",
            borderRadius: "12px", border: "none", background: "#0f172a", color: "#fff",
            fontWeight: 800, fontSize: "15px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          📷 Escanear todo ({despachadosSinCargar.length} pedido{despachadosSinCargar.length !== 1 ? "s" : ""} para cargar)
        </button>
      )}

      <div id="manifiesto-contenido" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

        {/* ── Pedidos DESPACHADOS ── */}
        {totalDespachados > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{
              padding: "12px 16px",
              background: todoCargado ? "#f0fdf4" : "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                {esCerrado ? "✅ Cargados en este viaje" : "📦 Para cargar"}
              </span>
              <span style={{
                fontSize: "12px", fontWeight: 600,
                color: todoCargado ? "#166534" : "#64748b",
                background: todoCargado ? "#dcfce7" : "#f1f5f9",
                padding: "3px 10px", borderRadius: "20px",
              }}>
                {todoCargado ? "✓ Todos cargados" : `${totalCargados}/${totalDespachados} cargados`}
              </span>
            </div>
            <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
              {despachados.map(p => (
                <PedidoCard
                  key={p.id}
                  p={p}
                  onScan={setScanPedido}
                  readonly={esCerrado}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Pedidos PENDIENTES de despacho (oculto si cerrado) ── */}
        {!esCerrado && totalPendientes > 0 && (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{
              padding: "12px 16px",
              background: "#fffbeb",
              borderBottom: "1px solid #fde68a",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, fontSize: "14px", color: "#92400e" }}>
                🕐 Falta despachar
              </span>
              <span style={{
                fontSize: "12px", fontWeight: 600, background: "#fef3c7",
                color: "#b45309", padding: "3px 10px", borderRadius: "20px",
              }}>
                {totalPendientes} pedido{totalPendientes !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {pendientes.map(p => {
                const esVentas = p.estado === "CONTROLADO";
                return (
                  <div key={p.id} style={{
                    background: "#fff", border: "1.5px solid #e2e8f0",
                    borderRadius: "10px", padding: "11px 14px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "13px", color: "#334155" }}>
                        {p.numero || p.id}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                        {p.cliente}
                      </div>
                      {esVentas && (
                        <div style={{ fontSize: "11px", color: "#b45309", marginTop: "3px", fontWeight: 600 }}>
                          ⚠️ Listo — falta que ventas despache
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      <span style={{
                        fontSize: "11px", fontWeight: 600,
                        background: esVentas ? "#fef3c7" : "#f1f5f9",
                        color: esVentas ? "#b45309" : "#64748b",
                        padding: "3px 8px", borderRadius: "20px",
                      }}>
                        {estadoLabel(p.estado)}
                      </span>
                      <button
                        onClick={() => quitarPedido(p.id)}
                        style={{
                          fontSize: "11px", color: "#94a3b8", background: "none",
                          border: "none", cursor: "pointer", textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Barra inferior fija ── */}
      {esCerrado ? (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "16px 20px", background: "rgba(255,255,255,0.95)",
          borderTop: "1px solid #e2e8f0", backdropFilter: "blur(8px)",
        }}>
          <button
            onClick={() => window.print()}
            className="btn btn--primary"
            style={{ width: "100%", padding: "14px", fontSize: "15px", fontWeight: 700 }}
          >
            🖨️ Imprimir manifiesto del camión
          </button>
        </div>
      ) : totalDespachados > 0 ? (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "14px 20px", background: "rgba(255,255,255,0.95)",
          borderTop: "1px solid #e2e8f0", backdropFilter: "blur(8px)",
        }}>
          <button
            onClick={() => setShowFinalizar(true)}
            style={{
              width: "100%", padding: "15px", fontSize: "15px", fontWeight: 800,
              borderRadius: "12px", border: "none",
              background: todoCargado ? "#16a34a" : "#0f172a", color: "#fff",
              cursor: "pointer",
            }}
          >
            🚚 Finalizar carga {!todoCargado && despachadosSinCargar.length + totalPendientes > 0 ? `(${despachadosSinCargar.length + totalPendientes} sin cargar)` : ""}
          </button>
        </div>
      ) : null}

      {/* ── FAB: agregar pedidos (solo si abierto) ── */}
      {!esCerrado && (
        <button
          onClick={abrirPicker}
          style={{
            position: "fixed", bottom: totalDespachados > 0 ? "84px" : "24px", right: "20px",
            background: "#0f172a", color: "#fff",
            border: "none", borderRadius: "28px",
            padding: "14px 20px", fontSize: "14px", fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
            zIndex: 100,
          }}
        >
          ＋ Agregar pedidos
        </button>
      )}

      {/* ── Modal picker: agregar pedidos ── */}
      {showPicker && (
        <div
          onClick={() => setShowPicker(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "flex-end",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", width: "100%",
              borderRadius: "16px 16px 0 0",
              padding: "20px", maxHeight: "80vh",
              display: "flex", flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>Agregar pedidos al camión</h3>
              <button
                onClick={() => setShowPicker(false)}
                style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}
              >×</button>
            </div>

            <input
              type="text"
              className="input"
              placeholder="Buscar por número o cliente…"
              value={searchPicker}
              onChange={e => setSearchPicker(e.target.value)}
              style={{ marginBottom: "12px", fontSize: "14px" }}
              autoFocus
            />

            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
              {loadingPicker ? (
                <div style={{ textAlign: "center", padding: "32px", color: "#94a3b8" }}>Buscando pedidos…</div>
              ) : pickerFiltrado.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px", color: "#94a3b8" }}>
                  {sq ? "No hay pedidos que coincidan" : "No hay pedidos disponibles para agregar"}
                </div>
              ) : (
                pickerFiltrado.map(p => {
                  const yaDespachado = p._col === "pedidos_despachados";
                  return (
                    <div key={p.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      background: "#f8fafc", border: "1.5px solid #e2e8f0",
                      borderRadius: "10px", padding: "12px 14px",
                    }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
                          {p.numero || p.id}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          {p.cliente}
                        </div>
                        <div style={{ fontSize: "11px", marginTop: "3px" }}>
                          {yaDespachado ? (
                            <span style={{ color: "#166534", fontWeight: 600, background: "#dcfce7", padding: "2px 8px", borderRadius: "10px" }}>
                              ✓ Despachado · listo para cargar
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>{estadoLabel(p.estado)}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => asignarPedido(p)}
                        disabled={asignando === p.id}
                        style={{
                          padding: "8px 16px", fontSize: "13px", fontWeight: 700,
                          background: asignando === p.id ? "#e2e8f0" : "#0f172a",
                          color: "#fff", border: "none", borderRadius: "8px",
                          cursor: asignando === p.id ? "not-allowed" : "pointer",
                          flexShrink: 0,
                        }}
                      >
                        {asignando === p.id ? "…" : "Agregar"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal scanner (un pedido) */}
      {scanPedido && (
        <ScanModal
          pedido={scanPedido}
          onClose={() => setScanPedido(null)}
          onCargado={() => {
            setScanPedido(null);
            showToast("✅ Carga confirmada");
          }}
        />
      )}

      {/* Escaneo continuo (todos los bultos seguidos) */}
      {showEscaneo && (
        <EscaneoContinuoModal
          pedidos={despachadosSinCargar}
          onClose={() => setShowEscaneo(false)}
        />
      )}

      {/* Modal finalizar carga */}
      {showFinalizar && (
        <FinalizarModal
          cargados={cargados}
          despachadosSinCargar={despachadosSinCargar}
          pendientes={pendientes}
          finalizando={finalizando}
          onClose={() => setShowFinalizar(false)}
          onConfirm={finalizarCarga}
        />
      )}

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #manifiesto-contenido, #manifiesto-contenido * { visibility: visible; }
          #control-header { visibility: visible !important; }
          #manifiesto-contenido {
            position: absolute; left: 0; top: 60px;
            width: 100%; padding: 0 20px;
          }
          #control-header {
            position: absolute; top: 0; left: 0; right: 0;
            box-shadow: none !important; padding: 8px 20px;
          }
          .btn { display: none !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}
